import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  LayoutDashboard, Building2, BarChart3, LogOut, 
  Menu, X, TrendingUp, Users, MapPin, Briefcase, FileText, DollarSign
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/zkteztq8_OrchardLMS%20Logo.png";

const PlatformAnalytics = () => {
  const { user, logout, token, API } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/platform/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/platform', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/platform/tenants', icon: Building2, label: 'Tenants' },
    { path: '/platform/financing-requests', icon: DollarSign, label: 'Financing' },
    { path: '/platform/analytics', icon: BarChart3, label: 'Analytics' },
  ];

  // Mock chart data based on analytics
  const planDistribution = [
    { name: 'Starter', value: Math.floor((analytics?.total_tenants || 0) * 0.5), color: '#2D5A27' },
    { name: 'Professional', value: Math.floor((analytics?.total_tenants || 0) * 0.35), color: '#F97316' },
    { name: 'Enterprise', value: Math.floor((analytics?.total_tenants || 0) * 0.15), color: '#3B82F6' },
  ];

  const monthlyGrowth = [
    { month: 'Jan', tenants: 10, properties: 120 },
    { month: 'Feb', tenants: 15, properties: 180 },
    { month: 'Mar', tenants: 22, properties: 280 },
    { month: 'Apr', tenants: 28, properties: 350 },
    { month: 'May', tenants: 35, properties: 420 },
    { month: 'Jun', tenants: analytics?.total_tenants || 40, properties: analytics?.total_properties || 500 },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-gray-900 transform transition-transform duration-200 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
            <img src={LOGO_URL} alt="Orchard LMS" className="h-8 w-auto brightness-0 invert" />
            <button className="lg:hidden text-gray-400 hover:text-white" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-4 py-3 border-b border-gray-800">
            <span className="text-xs font-medium text-secondary uppercase tracking-wide">Platform Admin</span>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-white text-sm font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-gray-800" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <div className="flex items-center gap-4">
              <button className="lg:hidden text-gray-500 hover:text-gray-700" onClick={() => setSidebarOpen(true)}>
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Platform Analytics</h1>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-gray-200" data-testid="metric-tenants">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Tenants</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics?.total_tenants || 0}</p>
                        <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <TrendingUp className="h-3 w-3" /> +12% this month
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200" data-testid="metric-users">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Users</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics?.total_users || 0}</p>
                        <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <TrendingUp className="h-3 w-3" /> +8% this month
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200" data-testid="metric-properties">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Properties</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics?.total_properties || 0}</p>
                        <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <TrendingUp className="h-3 w-3" /> +15% this month
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200" data-testid="metric-jobs">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Jobs</p>
                        <p className="text-2xl font-bold text-gray-900">{analytics?.total_jobs || 0}</p>
                        <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <TrendingUp className="h-3 w-3" /> +20% this month
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Briefcase className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Growth Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyGrowth}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                          <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                          <YAxis stroke="#6B7280" fontSize={12} />
                          <Tooltip />
                          <Bar dataKey="tenants" fill="#2D5A27" name="Tenants" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Plan Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 flex items-center justify-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={planDistribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {planDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Financial Module Stats */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg">Financial Module Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <DollarSign className="h-5 w-5 text-blue-600" />
                        <span className="font-medium text-gray-900">Line of Credit</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">{analytics?.loc_enabled_tenants || 0}</p>
                      <p className="text-sm text-gray-500">tenants enabled</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="h-5 w-5 text-purple-600" />
                        <span className="font-medium text-gray-900">Invoice Factoring</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-600">{analytics?.factoring_enabled_tenants || 0}</p>
                      <p className="text-sm text-gray-500">tenants enabled</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <Building2 className="h-5 w-5 text-gray-600" />
                        <span className="font-medium text-gray-900">Software Only</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-600">
                        {(analytics?.total_tenants || 0) - (analytics?.loc_enabled_tenants || 0) - (analytics?.factoring_enabled_tenants || 0)}
                      </p>
                      <p className="text-sm text-gray-500">tenants (no financial modules)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PlatformAnalytics;
